// Centralized fetch logging for all API requests
interface FetchLogEntry {
  timestamp: string;
  method: string;
  url: string;
  status?: number;
  duration: number;
  error?: string;
  headers?: Record<string, string>;
}

const logs: FetchLogEntry[] = [];
const MAX_LOGS = 100;

export function logFetch(entry: FetchLogEntry) {
  logs.push(entry);

  // Keep only last MAX_LOGS entries
  if (logs.length > MAX_LOGS) {
    logs.shift();
  }

  // Console log with styling
  const isDev = import.meta.env.MODE === 'development';
  if (isDev) {
    const statusColor =
      !entry.status ? '#ccc' :
      entry.status >= 200 && entry.status < 300 ? '#0a0' :
      entry.status >= 300 && entry.status < 400 ? '#08f' :
      entry.status >= 400 && entry.status < 500 ? '#f80' :
      '#f00';

    const statusText = entry.status ? ` ${entry.status}` : ' (pending)';
    const errorText = entry.error ? ` ERROR: ${entry.error}` : '';

    console.log(
      `%c[${entry.method}]%c ${entry.url}${statusText} (${entry.duration}ms)${errorText}`,
      `color: ${statusColor}; font-weight: bold`,
      'color: inherit'
    );
  }
}

export function getFetchLogs(): FetchLogEntry[] {
  return [...logs];
}

export function clearFetchLogs() {
  logs.length = 0;
}

/** A JSON call's limit: long enough for a cold Lambda, short enough to say so. */
export const DEFAULT_TIMEOUT_MS = 8_000;

/** The request ran past its time limit. Its message is shown to the player as is. */
export class FetchTimeoutError extends Error {
  constructor(public readonly timeoutMs: number) {
    super("The server didn't answer in time. Check your connection and try again.");
    this.name = "FetchTimeoutError";
  }
}

export interface FetchOptions {
  /** Milliseconds before the request is cancelled, or null for no limit. Defaults to DEFAULT_TIMEOUT_MS. */
  timeoutMs?: number | null;
}

/**
 * A signal that aborts after `ms`. An abort cancels the request itself;
 * racing a promise against a timer would reject the caller and leave the
 * connection running. The fallback covers anywhere AbortSignal.timeout is
 * missing.
 */
export function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(new DOMException("The operation timed out.", "TimeoutError")), ms);
  return controller.signal;
}

/** True for the rejection a timed-out or cancelled fetch produces. */
export function isAbort(err: unknown): boolean {
  const name = (err as { name?: string })?.name;
  if (name === "AbortError" || name === "TimeoutError" || err instanceof FetchTimeoutError) return true;
  const message = (err as { message?: string })?.message ?? "";
  return /abort/i.test(message);
}

/** The caller's signal and the time limit, as one. */
function combine(signals: AbortSignal[]): AbortSignal {
  if (signals.length === 1) return signals[0];
  if (typeof AbortSignal.any === "function") return AbortSignal.any(signals);
  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    signal.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}

/**
 * fetch, logged, with a time limit. The limit also covers reading the body,
 * since the signal stays attached to the response. A timeout rejects with
 * FetchTimeoutError; the caller's own abort rejects as fetch would.
 */
export async function fetchWithLogging(
  input: RequestInfo | URL,
  init?: RequestInit,
  { timeoutMs = DEFAULT_TIMEOUT_MS }: FetchOptions = {},
): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
  const method = init?.method || 'GET';
  const startTime = performance.now();
  const timer = timeoutMs === null ? null : timeoutSignal(timeoutMs);
  const signals = [init?.signal, timer].filter((s): s is AbortSignal => !!s);
  const signal = signals.length ? combine(signals) : undefined;

  try {
    const response = await fetch(input, signal ? { ...init, signal } : init);
    const duration = Math.round(performance.now() - startTime);

    logFetch({
      timestamp: new Date().toISOString(),
      method,
      url,
      status: response.status,
      duration,
    });

    return response;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    // The limit ran out, not the caller's patience.
    const timedOut = !!timer?.aborted && !init?.signal?.aborted && timeoutMs !== null;
    const thrown = timedOut ? new FetchTimeoutError(timeoutMs) : error;
    const errorMessage = thrown instanceof Error ? thrown.message : String(thrown);

    logFetch({
      timestamp: new Date().toISOString(),
      method,
      url,
      duration,
      error: errorMessage,
    });

    throw thrown;
  }
}

// Export a console command to view logs
if (typeof window !== 'undefined') {
  Object.assign(window, { getFetchLogs, clearFetchLogs });
}
